import React, { useContext, useEffect, useState, useMemo } from 'react'
import injectStyles from 'react-jss'
import { Grid } from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { userLogin, userLoginOAuth2 } from 'Reducers/Auth/actions'
import { getCountries } from '../../../Reducers/Countries/actions'
import { EDDM_SIGNUP, ROLE_CLIENT_ADMIN } from '../../../shared/constants'
import { gtagWrapper, postHubSpot } from '../../../Utils/Functions'
import { PATHS } from '../../../Config/Routing/paths'
import useTrans from '../../../Utils/Hooks/useTrans'
import useQuery from '../../../Utils/Hooks/useQuery'

import Loader from '../../Campaigns/Page/Codes/components/CommonComponents/Loader'

import AuthWrapper from '../AuthWrapper'
import { SnackBarContext } from '../../../Components/SnackBar'
import FormSteps from './components/FormSteps'
import styles from './styles.js'
import { STEPS, getInitialSignupData } from './options'
import { createClientByPlatform } from './graphQL'
import { selectCountriesToValues } from './selectors'
import { useWrappedPosthog } from '../../../Components/PostHogProvider/hooks.js'
import { authorization } from '../../../Module/Auth/Auth.js'
import { AUTH_METHOD } from '../../../constants.js'
import { CustomersTestimonials } from '../components/CustomersTestimonials/CustomerTestimonials.jsx'
import { getSignupSource, getIsEddmUser } from '../../../Utils/User'
import {
  getUserCountry,
  isWorkEmail,
  isPersonalEmail,
} from '../../../Utils/EmailValidation'
import { getNextLink } from '../../../Utils/Link'

const SignUp = ({ classes }) => {
  const runSnackBar = useContext(SnackBarContext)
  const posthog = useWrappedPosthog()
  const query = useQuery()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const countries = useSelector(selectCountriesToValues) || []
  const [step, setStep] = useState(STEPS.STEP_1)
  const [isLoading, setIsLoading] = useState(false)
  const transPage = location.pathname.split('/')[1] || ''
  const { t } = useTrans({ page: transPage })
  const [authMethod, setAuthMethod] = useState(AUTH_METHOD.OPPIZI)
  const [userCountry, setUserCountry] = useState(null)
  const [shouldRestrictToEddmOnly, setShouldRestrictToEddmOnly] = useState(
    false,
  )

  const openSnackBar = (type, message) => {
    runSnackBar({
      type: type,
      vertical: 'top',
      horizontal: 'center',
      msg: message,
      autoHideDuration: 6000,
      key: new Date().getTime(),
    })
  }

  const getToolsParams = () => {
    try {
      const oppizi_source = query.get('oppizi_source')
      const qr_code_id = query.get('qr_code_id')
      const status = query.get('status')
      const credentialId = query.get('credentialId')
      const shopName = query.get('shopName')
      const shopDomain = query.get('shopDomain')
      return {
        oppizi_source,
        qr_code_id,
        status,
        credentialId,
        shopName,
        shopDomain,
      }
    } catch (error) {
      return {
        oppizi_source: null,
        qr_code_id: null,
        status: null,
        credentialId: null,
        shopName: null,
        shopDomain: null,
      }
    }
  }

  const toolsParams = useMemo(() => getToolsParams(), [query])
  const {
    oppizi_source,
    qr_code_id,
    status,
    credentialId,
    shopName,
    shopDomain,
  } = toolsParams

  const isEDDMUser = getIsEddmUser(oppizi_source)

  const [fields, setFields] = useState(getInitialSignupData(isEDDMUser, query))

  const onChange = type => (value, error, additionalError = false) => {
    setFields(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        value: value === null ? prev[type].value : value,
        error: !!error,
        additionalError,
      },
    }))
    if (type === 'firstName' && value) {
      setFields(prev => ({
        ...prev,
        firstName: {
          ...prev.firstName,
          error: false,
        },
      }))
    }
    if (type === 'lastName' && value) {
      setFields(prev => ({
        ...prev,
        lastName: {
          ...prev.lastName,
          error: false,
        },
      }))
    }
  }

  const onSubmit = async e => {
    e.preventDefault()
    const source = shouldRestrictToEddmOnly
      ? EDDM_SIGNUP
      : getSignupSource(oppizi_source)
    try {
      setIsLoading(true)
      const response = await createClientByPlatform({
        authMethod: authMethod,
        firstName: fields.firstName.value.trim(),
        lastName: fields.lastName.value.trim(),
        email: fields.email.value.trim(),
        password: fields.password.value,
        role: ROLE_CLIENT_ADMIN,
        mobilePhone: fields.phone.value,
        companyName: fields.companyName.value.trim(),
        countryId: fields.countryId.value,
        isSelfServeEnabled: true,
        createdBy: source,
        providerType: fields.providerType.value.trim(),
        providerUserId: fields.providerUserId.value.trim(),
      })
      const {
        data: { createClientFromPlatform },
      } = await response.json()

      gtagWrapper({
        event: source,
        client_id: createClientFromPlatform.clientId.toString(),
        user_id: createClientFromPlatform.userId.toString(),
        country: fields.countryId.value
          ? countries.find(item => item.value === fields.countryId.value).name
          : '',
        firstname: fields.firstName.value.trim(),
        lastname: fields.lastName.value.trim(),
        email: fields.email.value.trim(),
        phone: fields.phone.value.trim(),
        oppizi_source: source,
        qr_code_id,
      })

      if (posthog) {
        posthog.identify(createClientFromPlatform.userId.toString(), {
          client_id: createClientFromPlatform.clientId.toString(),
          user_id: createClientFromPlatform.userId.toString(),
          country: fields.countryId.value
            ? countries.find(item => item.value === fields.countryId.value).name
            : '',
          firstname: fields.firstName.value.trim(),
          lastname: fields.lastName.value.trim(),
          email: fields.email.value.trim(),
          phone: fields.phone.value.trim(),
          signup_date: new Date().toISOString(),
          oppizi_source: source,
          qr_code_id,
        })

        posthog.capture('Signed Up', {
          'signup_type': !fields.password.value ? 'Social' : 'Manual Form',
          'email': fields.email.value.trim(),
          'signup_date': new Date().toISOString(),
          'role': ROLE_CLIENT_ADMIN,
          'client_id': createClientFromPlatform.clientId.toString(),
          'company_name': fields.companyName.value.trim(),
          'country': fields.countryId.value
            ? countries.find(item => item.value === fields.countryId.value).name
            : '',
          'oppizi_source': source,
          'firstname': fields.firstName.value.trim(),
          'lastname': fields.lastName.value.trim(),
        })
      }

      await postHubSpot({
        pipeline: isEDDMUser ? 'EDDM' : 'PromofyAds',
        stage_id: 0, // required
        campaign_id: '', // required
        company_id: createClientFromPlatform.clientId, // required
        contact_id: createClientFromPlatform.userId, // required
        email: fields.email.value.trim(), // required
        firstname: fields.firstName.value.trim(),
        lastname: fields.lastName.value.trim(),
        phone: fields.phone.value.trim(),
        country: fields.countryId.value
          ? countries.find(item => item.value === fields.countryId.value).name
          : '',
        company_name: fields.companyName.value.trim(),
        campaign_name: '',
        total_costs: '',
        currency: '',
        oppizi_source: source,
      })

      if (authMethod === AUTH_METHOD.SOCIAL) {
        localStorage.setItem('apiKey', createClientFromPlatform.authToken)
        dispatch(userLoginOAuth2(fields.email.value))
      } else {
        // DEFAULT LOGIN TYPE
        dispatch(userLogin(fields.email.value, fields.password.value))
      }
    } catch (e) {
      console.warn('Error:', e)
    } finally {
      setIsLoading(false)
    }
  }

  const onBack = () => {
    history.push(
      PATHS.main.login +
        (status && credentialId
          ? `?status=${status}&credentialId=${credentialId}&shopName=${shopName}&shopDomain=${shopDomain}`
          : ''),
    )
  }

  const validateSocialSignupEmail = email => {
    if (!userCountry || !email) return false
    if (isEDDMUser) {
      return true
    } else if (userCountry.countryCode !== 'US') {
      return isWorkEmail(email)
    } else {
      const isPersonal = isPersonalEmail(email)
      setShouldRestrictToEddmOnly(isPersonal)
      return true
    }
  }

  useEffect(() => {
    const detectCountry = async () => {
      const country = await getUserCountry()
      setUserCountry(country)
    }
    detectCountry()
    if (fields.email.value && query.get('email')) {
      const isPersonal = isPersonalEmail(fields.email.value)
      setShouldRestrictToEddmOnly(isPersonal)
    }
  }, [])

  useEffect(() => {
    dispatch(getCountries())
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      if (authorization.isLogged()) {
        if (status && credentialId) {
          window.location.href = getNextLink(
            `/dashboard/callbacks/shopify?status=${status}&credentialId=${credentialId}&shopName=${shopName}&shopDomain=${shopDomain}`,
          )
        } else {
          history.push(PATHS.main.dashboard)
        }
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [status, credentialId, shopName, shopDomain])

  useEffect(() => {
    const handleStorageChange = e => {
      if (e.key === 'signUpData') {
        const updatedSignUpData = JSON.parse(e.newValue)
        if (!validateSocialSignupEmail(updatedSignUpData.email)) {
          openSnackBar('error', 'Please use a work email to continue')
          localStorage.removeItem('signUpData')
          return
        }
        setAuthMethod(AUTH_METHOD.SOCIAL)
        if (posthog) {
          posthog.capture('User got authenticated', {
            'event_action':
              'User Selects email on google popup and gets authenticated',
            'event_category': 'Social Signup',
          })
        }
        setFields(prev => ({
          ...prev,
          firstName: {
            ...prev.firstName,
            value: updatedSignUpData.firstName || prev.firstName.value,
          },
          lastName: {
            ...prev.lastName,
            value: updatedSignUpData.lastName || prev.lastName.value,
          },
          email: {
            ...prev.email,
            value: updatedSignUpData.email || prev.email.value,
          },
          providerUserId: {
            ...prev.providerUserId,
            value:
              updatedSignUpData.providerUserId || prev.providerUserId.value,
          },
          providerType: {
            ...prev.providerType,
            value: updatedSignUpData.providerType || prev.providerType.value,
          },
        }))
        setStep(STEPS.STEP_2)
        localStorage.removeItem('signUpData')
      }
    }

    if (!userCountry) return

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [userCountry])

  useEffect(() => {
    if (location.state && userCountry) {
      const signUpData = location.state.signUpData
      if (signUpData) {
        if (!validateSocialSignupEmail(signUpData.email)) {
          openSnackBar('error', 'Please use a work email to continue')
          localStorage.removeItem('signUpData')
          location.state.signUpData = null
          return
        }
        setAuthMethod(AUTH_METHOD.SOCIAL)
        setFields(prev => ({
          ...prev,
          firstName: {
            ...prev.firstName,
            value: signUpData.firstName || prev.firstName.value,
          },
          lastName: {
            ...prev.lastName,
            value: signUpData.lastName || prev.lastName.value,
          },
          email: {
            ...prev.email,
            value: signUpData.email || prev.email.value,
          },
          providerUserId: {
            ...prev.providerUserId,
            value: signUpData.providerUserId || prev.providerUserId.value,
          },
          providerType: {
            ...prev.providerType,
            value: signUpData.providerType || prev.providerType.value,
          },
        }))
        setStep(STEPS.STEP_2)
        localStorage.removeItem('signUpData')
        location.state.signUpData = null
      }
    }
  }, [location.state, userCountry])

  return (
    <AuthWrapper>
      {isLoading ? <Loader /> : null}
      <Grid container spacing={4}>
        <Grid container item xs={12} md={6}>
          <Grid container className={'content'}>
            <FormSteps
              authMethod={authMethod}
              fields={fields}
              setFields={setFields}
              classes={classes}
              step={step}
              countries={countries}
              onChange={onChange}
              onBack={onBack}
              onSubmit={onSubmit}
              setStep={setStep}
              t={t}
              isEDDMUser={isEDDMUser}
              userCountry={userCountry}
              shouldRestrictToEddmOnly={shouldRestrictToEddmOnly}
              setShouldRestrictToEddmOnly={setShouldRestrictToEddmOnly}
              toolsParams={toolsParams}
            />
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          md={6}
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          <CustomersTestimonials />
        </Grid>
      </Grid>
    </AuthWrapper>
  )
}

export default injectStyles(styles)(SignUp)
